// HEAL-44a §5.2 — просмотр исходного бланка анализа.
//
// Ограничение архитектуры реальное: приложение — это веб внутри нативной
// оболочки, а Android в таком окне PDF не показывает вообще, iOS показывает
// нестабильно. Поэтому три ветки:
//
//   фото              → свой полноэкранный просмотрщик внутри приложения
//                       (это большинство домашних загрузок, отдавать их
//                        системе — плохой UX)
//   PDF, нативка      → нативно скачать во временную папку → системный
//                       просмотрщик (QuickLook на iOS, выбор на Android)
//   PDF, веб          → новая вкладка, браузер справится сам

import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { api } from '@/api/client';
import type { UploadFileResponse } from '@/api/types';

export type ViewFileResult =
  | { kind: 'image'; url: string }
  | { kind: 'opened' }
  | { kind: 'unavailable' };

export type ViewFileError = 'unavailable' | 'failed';

/**
 * Открывает исходный файл анализа.
 *
 * Для изображения возвращает подписанную ссылку — вызывающий показывает её
 * собственным просмотрщиком. Для PDF открывает системными средствами и
 * возвращает `opened`.
 */
export async function openUploadFile(
  uploadId: number,
  /** Не используется с переходом на нативную загрузку: файл больше не проходит
   *  через память WebView, и порога по размеру нет. Параметр оставлен, чтобы не
   *  трогать два экрана ради подписи. */
  _fileSize?: number,
  /** HEAL-78 — вкладка, открытая заранее, прямо в обработчике нажатия (см.
   *  `preopenPdfTab`). Если передана, ссылка доставляется в неё; при любом
   *  исходе, кроме `opened`, закрывать её — забота вызывающего. */
  preopenedTab?: Window | null,
): Promise<ViewFileResult> {
  let file: UploadFileResponse;

  try {
    file = (await api.get<UploadFileResponse>(`/uploads/${uploadId}/file`)).data;
  } catch (e) {
    // 410 — запись есть, файла на диске нет. Человеку нужно объяснение, а не
    // пустой экран, поэтому отличаем этот случай от сетевого сбоя.
    const status = (e as { response?: { status?: number } }).response?.status;
    if (status === 410) return { kind: 'unavailable' };
    throw e;
  }

  if (file.mime_type.startsWith('image/')) {
    return { kind: 'image', url: file.signed_url };
  }

  if (!Capacitor.isNativePlatform()) {
    if (preopenedTab) {
      preopenedTab.location.href = file.signed_url;
    } else {
      window.open(file.signed_url, '_blank', 'noopener,noreferrer');
    }
    return { kind: 'opened' };
  }

  try {
    await openNativePdf(file.signed_url, file.mime_type, uploadId);
  } catch (e) {
    // Один повтор со свежей ссылкой.
    //
    // Раньше повторялся только явный признак «ссылка протухла» — его давал
    // разбор HTTP-статуса, который нативная загрузка наружу не отдаёт. Теперь
    // повторяем на любом отказе загрузки, один раз: ссылка живёт пять минут,
    // самая вероятная причина — именно она, а второй поход по свежей ссылке
    // стоит секунды против ошибки там, где всё поправимо.
    //
    // На отказ системного просмотрщика повтор не тратится: файл уже на диске,
    // и второй раз он откажется так же.
    if (isOpenStepFailure(e)) throw e;

    const retry = (await api.get<UploadFileResponse>(`/uploads/${uploadId}/file`)).data;
    await openNativePdf(retry.signed_url, retry.mime_type, uploadId);
  }

  return { kind: 'opened' };
}

/**
 * HEAL-78 — открыть пустую вкладку под PDF ПРЯМО В ОБРАБОТЧИКЕ НАЖАТИЯ.
 *
 * Браузер разрешает новую вкладку, только пока идёт обработка жеста. Ссылку на
 * бланк мы получаем запросом, и `window.open` после `await` жест уже потерял —
 * Safari и Chrome на iPhone рубят его как рекламный поп-ап. Человек видит, что
 * по кнопке ничего не произошло, и читает это как «бланк не открывается».
 * Поэтому вкладка открывается сразу, а ссылка приходит в неё потом — тот же
 * приём, что у вложений визитов (`VisitDetail`).
 *
 * `opener` обнуляется вручную: с `noopener` браузер вернул бы `null`, и
 * доставить ссылку было бы некуда.
 *
 * Только веб и только PDF. На нативе вкладок нет, а фото показывается своим
 * просмотрщиком — заранее открытая вкладка осталась бы пустой.
 */
export function preopenPdfTab(mimeType: string | undefined): Window | null {
  if (Capacitor.isNativePlatform() || mimeType !== 'application/pdf') return null;

  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  return tab;
}

function isOpenStepFailure(e: unknown): boolean {
  return e instanceof Error && e.message.startsWith('step=open');
}

/**
 * Пометить шаг в сообщении об ошибке.
 *
 * Исходное сообщение сохраняется целиком: у нативных плагинов там код и текст
 * системы, и именно они говорят, что делать дальше.
 */
async function step<T>(name: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    const original = e instanceof Error ? e.message : String(e);
    const tagged = new Error(`step=${name} ${original}`);
    (tagged as { code?: unknown }).code = (e as { code?: unknown })?.code;
    throw tagged;
  }
}

async function openNativePdf(url: string, mimeType: string, uploadId: number): Promise<void> {
  // ⚠️ Качает НАТИВНЫЙ слой, а не WebView, и это не оптимизация, а
  // единственный работающий путь.
  //
  // `fetch` из WebView идёт с origin `capacitor://localhost` на
  // api.healthlab.kz, то есть кросс-доменно. CORS у нас покрывает `/api/*`, а
  // ссылка на бланк ведёт в веб-маршрут `/uploads-signed/...` — заголовков там
  // нет, и WebKit рубит ответ, даже когда сервер честно отдал файл. Наружу это
  // выглядит как `Load failed`, а в логах сервера — как успешный запрос: самая
  // неприятная разновидность поломки. Найдено на живом iPhone 24.09.
  //
  // Заодно снимается весь класс проблем с памятью: файл не превращается ни в
  // blob, ни в base64 (+37 % к размеру), а сразу ложится на диск. Из-за этого
  // исчезла ветка «больше 4 МБ — открыть в браузере»: порог существовал только
  // чтобы не уронить WebView на крупном бланке, а ронять стало нечего.
  const downloaded = await step('download', () => Filesystem.downloadFile({
    url,
    // Только Cache — не «Документы» и не общая папка загрузок. Иначе бланк
    // анализа окажется в файловом менеджере телефона и в облачном бэкапе.
    path: `healthlab-analysis-${uploadId}.pdf`,
    directory: Directory.Cache,
  }));

  if (!downloaded.path) {
    throw new Error('download_no_path');
  }

  await step('open', () => FileOpener.open({ filePath: downloaded.path!, contentType: mimeType }));
}

/** Blob → чистый base64 (без `data:...;base64,` префикса). Зеркало lib/share.ts. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}
